import React from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { 
  Home, ClipboardList, UserPlus, Users, Trophy, BarChart3, LogOut, Settings
} from "lucide-react";

// Contestant Pages
import ContestantHomePage from "./ContestantHomePage";
import RegistrationPage from "./RegistrationPage";
import IndividualMembershipPage from "./IndividualMembershipPage";
import FamilyMembershipPage from "./FamilyMembershipPage";
import ContestantStatsPage from "./ContestantStatsPage";
import LiveResultsPage from "./LiveResultsPage";
import SettingsPage from "./SettingsPage";

const navItems = [
  { path: "", icon: Home, label: "Home" },
  { path: "register", icon: ClipboardList, label: "Register" },
  { path: "individual-membership", icon: UserPlus, label: "Individual" },
  { path: "family-membership", icon: Users, label: "Family" },
  { path: "stats", icon: BarChart3, label: "My Stats" },
  { path: "live", icon: Trophy, label: "Live" },
  { path: "settings", icon: Settings, label: "Settings" },
];

const ContestantDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isActive = (path) => {
    const currentPath = location.pathname.replace("/contestant", "").replace(/^\//, "");
    return currentPath === path;
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0" data-testid="contestant-dashboard">
      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-primary/20">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <span className="font-heading font-bold text-primary-foreground">TTP</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-text-primary">TTP Play Day</h1>
              <p className="text-xs text-text-secondary">Welcome, {user?.first_name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-text-secondary hover:text-text-primary"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      {/* Desktop Navigation */}
      <nav className="hidden md:block sticky top-[61px] z-40 bg-white border-b border-primary/20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={`/contestant/${item.path}`}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    isActive(item.path)
                      ? "border-primary text-primary-foreground bg-primary/5"
                      : "border-transparent text-text-secondary hover:text-text-primary hover:bg-secondary/50"
                  }`}
                  data-testid={`nav-${item.path || 'home'}`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <Routes>
          <Route index element={<ContestantHomePage />} />
          <Route path="register" element={<RegistrationPage />} />
          <Route path="individual-membership" element={<IndividualMembershipPage />} />
          <Route path="family-membership" element={<FamilyMembershipPage />} />
          <Route path="stats" element={<ContestantStatsPage />} />
          <Route path="live" element={<LiveResultsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Routes>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-primary/20 z-50">
        <div className="grid grid-cols-7 gap-1 px-2 py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={`/contestant/${item.path}`}
                className={`flex flex-col items-center py-2 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? "bg-primary text-primary-foreground"
                    : "text-text-secondary hover:bg-secondary"
                }`}
                data-testid={`mobile-nav-${item.path || 'home'}`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] mt-1">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default ContestantDashboard;
